import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useEmployeeDashboard } from '@/hooks/useEmployeeDashboard';
import { PasswordChangeDialog } from '@/components/employee/PasswordChangeDialog';
import { ProfileImageUpload } from '@/components/employee/ProfileImageUpload';
import { format } from 'date-fns';
import { User, Mail, Phone, Calendar, MapPin, Briefcase, GraduationCap, Award, Shield, Key, BarChart3, FileText, Clock, DollarSign, Users, CheckCircle } from 'lucide-react';
const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'yyyy/MM/dd');
  } catch {
    return dateString;
  }
};
export default function MyProfile() {
  const {
    user
  } = useAuth();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  // استخدام hook dashboard للحصول على بيانات الموظف الحالي والتحليلات الخاصة به
  const {
    data: dashboardData,
    isLoading,
    error,
    refetch
  } = useEmployeeDashboard(user?.employeeData?.id);
  if (isLoading) {
    return <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="animate-spin mx-auto h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
              <h3 className="mt-4 text-lg font-semibold">جاري تحميل ملفك الشخصي...</h3>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  if (error) {
    return <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <User className="mx-auto h-12 w-12 text-destructive" />
              <h3 className="mt-4 text-lg font-semibold">خطأ في تحميل الملف الشخصي</h3>
              <p className="text-muted-foreground">{error?.message || 'حدث خطأ غير متوقع'}</p>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  if (!dashboardData?.employee) {
    return <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <User className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">لم يتم العثور على ملفك الشخصي</h3>
              <p className="text-muted-foreground">
                يرجى التواصل مع إدارة النظام لحل هذه المشكلة.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  const employee = dashboardData.employee;
  const stats = dashboardData.stats;

  const handleImageUpdate = async (imageUrl: string | null) => {
    // The image is already updated in the database by ProfileImageUpload component
    // We can trigger a refetch of employee data here if needed
    console.log('Profile image updated:', imageUrl);
    // Refetch dashboard data to update the displayed image
    await refetch();
  };
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'present': {
        label: 'حاضر',
        variant: 'default' as const
      },
      'absent': {
        label: 'غائب',
        variant: 'destructive' as const
      },
      'late': {
        label: 'متأخر',
        variant: 'secondary' as const
      }
    };
    const config = statusConfig[status as keyof typeof statusConfig] || {
      label: status,
      variant: 'outline' as const
    };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };
  return <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">ملفي الشخصي</h1>
          
        </div>
      </div>

      <Tabs defaultValue="personal" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="personal">المعلومات الشخصية</TabsTrigger>
          <TabsTrigger value="photo">الصورة الشخصية</TabsTrigger>
          <TabsTrigger value="job">المعلومات الوظيفية</TabsTrigger>
          <TabsTrigger value="performance">أدائي وإحصائياتي</TabsTrigger>
        </TabsList>

        {/* المعلومات الشخصية */}
        <TabsContent value="personal" className="space-y-6">
          {/* بطاقة الملف الشخصي */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                الملف الشخصي
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6 mb-6">
                <Avatar className="w-24 h-24">
                  {employee.profile_image && <AvatarImage src={employee.profile_image} alt={employee.name} />}
                  <AvatarFallback className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white font-semibold text-lg">
                    {employee.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold">{employee.name}</h2>
                  <p className="text-muted-foreground">{employee.position}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {getStatusBadge(employee.status)}
                  </div>
                </div>
                <Button onClick={() => setPasswordDialogOpen(true)} className="flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  تغيير كلمة المرور
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">البريد الإلكتروني</p>
                      <p className="text-muted-foreground">{employee.email || 'غير محدد'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">رقم الهاتف</p>
                      <p className="text-muted-foreground">{employee.phone || 'غير محدد'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">تاريخ الميلاد</p>
                      <p className="text-muted-foreground">
                        {employee.birth_date ? formatDate(employee.birth_date) : 'غير محدد'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">القسم</p>
                      <p className="text-muted-foreground">{employee.department || 'غير محدد'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">تاريخ الانضمام</p>
                      <p className="text-muted-foreground">{formatDate(employee.join_date)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">ساعات العمل</p>
                      <p className="text-muted-foreground">
                        {employee.working_hours ? `${employee.working_hours} ساعة/يوم` : 'غير محدد'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* المؤهلات والخبرات */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5" />
                المؤهلات والخبرات
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <Award className="w-4 h-4" />
                  المؤهلات العلمية
                </h4>
                <p className="text-muted-foreground">
                  {employee.qualifications || 'لم يتم إدخال معلومات المؤهلات'}
                </p>
              </div>

              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <Briefcase className="w-4 h-4" />
                  الخبرات السابقة
                </h4>
                <p className="text-muted-foreground">
                  {employee.previous_experience || 'لم يتم إدخال معلومات الخبرات السابقة'}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* تبويب الصورة الشخصية */}
        <TabsContent value="photo" className="space-y-6">
          <ProfileImageUpload 
            currentImage={employee.profile_image}
            employeeName={employee.name}
            employeeId={employee.id}
            onImageUpdate={handleImageUpdate}
          />
        </TabsContent>

        {/* المعلومات الوظيفية */}
        <TabsContent value="job" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                المعلومات الوظيفية
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="font-medium">رقم الموظف</p>
                    <p className="text-muted-foreground">{employee.employee_number || 'غير محدد'}</p>
                  </div>

                  <div>
                    <p className="font-medium">المسمى الوظيفي</p>
                    <p className="text-muted-foreground">{employee.job_title || 'غير محدد'}</p>
                  </div>

                  <div>
                    <p className="font-medium">تاريخ التوظيف</p>
                    <p className="text-muted-foreground">
                      {employee.hiring_date ? formatDate(employee.hiring_date) : 'غير محدد'}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="font-medium">المسؤوليات الوظيفية</p>
                    <p className="text-muted-foreground">
                      {employee.job_responsibilities || 'لم يتم تحديد المسؤوليات'}
                    </p>
                  </div>

                  <div>
                    <p className="font-medium">الوصف الوظيفي</p>
                    <p className="text-muted-foreground">
                      {employee.job_description || 'لم يتم تحديد الوصف الوظيفي'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* الأداء والإحصائيات */}
        <TabsContent value="performance">
          <div className="space-y-6">
            {/* إحصائياتي */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <Users className="h-8 w-8 text-blue-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">العملاء الإجمالي</p>
                      <p className="text-2xl font-bold">{stats.totalClients}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">العملاء النشطون</p>
                      <p className="text-2xl font-bold">{stats.activeClients}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <Briefcase className="h-8 w-8 text-purple-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">الباقات المكتملة</p>
                      <p className="text-2xl font-bold">{stats.completedPackages}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center">
                    <Clock className="h-8 w-8 text-orange-600" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-muted-foreground">المهام المعلقة</p>
                      <p className="text-2xl font-bold">{stats.pendingTasks}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* مؤشر الأداء الشهري */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  الأداء الشهري
                </CardTitle>
                <CardDescription>
                  نسبة إنجاز المهام خلال الشهر الماضي
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center p-8">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {stats.monthlyPerformance}%
                    </div>
                    <p className="text-muted-foreground">معدل الإنجاز</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* عملائي */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  عملائي ({dashboardData.clients.length})
                </CardTitle>
                <CardDescription>
                  العملاء الذين قمت بإنشائهم أو أتابعهم
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData.clients.length > 0 ? <div className="space-y-3">
                    {dashboardData.clients.slice(0, 5).map(client => <div key={client.id} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <h4 className="font-medium">{client.name_ar}</h4>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(client.created_at)}
                          </p>
                        </div>
                        <Badge variant="outline">{client.status}</Badge>
                      </div>)}
                    {dashboardData.clients.length > 5 && <p className="text-sm text-muted-foreground text-center">
                        وعملاء آخرون... ({dashboardData.clients.length - 5})
                      </p>}
                  </div> : <p className="text-muted-foreground text-center py-8">
                    لم تقم بإنشاء أي عملاء بعد
                  </p>}
              </CardContent>
            </Card>

            {/* مهامي */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  مهامي الحديثة ({dashboardData.tasks.length})
                </CardTitle>
                <CardDescription>
                  آخر المهام المسندة إليك
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardData.tasks.length > 0 ? <div className="space-y-3">
                    {dashboardData.tasks.slice(0, 5).map(task => <div key={task.id} className="flex items-center justify-between p-3 border rounded">
                        <div>
                          <h4 className="font-medium">{task.title}</h4>
                          <p className="text-sm text-muted-foreground">
                            {task.due_date ? `موعد التسليم: ${formatDate(task.due_date)}` : 'بدون موعد محدد'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{task.priority}</Badge>
                          <Badge variant={task.status === 'completed' ? 'default' : 'secondary'}>
                            {task.status}
                          </Badge>
                        </div>
                      </div>)}
                    {dashboardData.tasks.length > 5 && <p className="text-sm text-muted-foreground text-center">
                        ومهام أخرى... ({dashboardData.tasks.length - 5})
                      </p>}
                  </div> : <p className="text-muted-foreground text-center py-8">
                    لا توجد مهام مسندة إليك حالياً
                  </p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* حوار تغيير كلمة المرور */}
      <PasswordChangeDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen} employeeId={employee.id} currentEmail={employee.email} />
    </div>;
}